import { getScriptTurnText } from "@/types/ScriptTurn"

import type { EditableTestCase } from "../model"

export interface FindReplaceMatch {
  testCaseId: string
  field:
    | "input_str"
    | "expected_behaviors"
    | "expected_output_json"
    | "script_turns"
  fieldKey?: string
  matchIndex: number
  startIndex: number
  endIndex: number
  text: string
}

function appendMatches(
  matches: FindReplaceMatch[],
  regex: RegExp,
  text: string,
  details: Pick<FindReplaceMatch, "testCaseId" | "field" | "fieldKey">,
  initialMatchIndex = 0,
): number {
  let match: RegExpExecArray | null
  let matchIndex = initialMatchIndex

  while ((match = regex.exec(text)) !== null) {
    matches.push({
      ...details,
      matchIndex: matchIndex++,
      startIndex: match.index,
      endIndex: match.index + match[0].length,
      text: match[0],
    })
  }
  regex.lastIndex = 0
  return matchIndex
}

export function findTestCaseMatches(
  testCases: EditableTestCase[],
  searchText: string,
): FindReplaceMatch[] {
  if (!searchText.trim()) {
    return []
  }

  const matches: FindReplaceMatch[] = []
  const regex = new RegExp(
    searchText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
    "gi",
  )

  testCases.forEach((testCase) => {
    if (testCase.input_str) {
      appendMatches(matches, regex, testCase.input_str, {
        testCaseId: testCase.id,
        field: "input_str",
      })
    }

    testCase.script_turns?.forEach((turn, turnIndex) => {
      const text = getScriptTurnText(turn)
      if (text === null) {
        return
      }
      appendMatches(matches, regex, text, {
        testCaseId: testCase.id,
        field: "script_turns",
        fieldKey: turnIndex.toString(),
      })
    })

    testCase.expected_behaviors.forEach((behavior, behaviorIndex) => {
      appendMatches(matches, regex, behavior, {
        testCaseId: testCase.id,
        field: "expected_behaviors",
        fieldKey: behaviorIndex.toString(),
      })
    })

    if (!testCase.expected_output_json) {
      return
    }
    Object.entries(testCase.expected_output_json).forEach(([key, value]) => {
      const matchIndex = appendMatches(matches, regex, key, {
        testCaseId: testCase.id,
        field: "expected_output_json",
        fieldKey: `${key}:key`,
      })
      if (typeof value === "string") {
        appendMatches(
          matches,
          regex,
          value,
          {
            testCaseId: testCase.id,
            field: "expected_output_json",
            fieldKey: `${key}:value`,
          },
          matchIndex,
        )
      }
    })
  })

  return matches
}
